import { STEP_FALLBACK, shortStep } from 'views/sessions/deck';
import type { IStep } from 'views/design-system';

/**
 * Investor-row shaping for the fund detail page.
 *
 * Every sum here is the SAME arithmetic `fund-header.tsx` does today
 * (`getTotalValue` / `getPendingValue`) — deliberately, so the strip cannot
 * quote a figure the table does not. What changes is where it is read from:
 * `FundDetailState`, the payload the table itself renders, rather than the
 * `FundInvestors` atom that only the list's row-click ever writes (spec §5.1.1).
 */

export interface IInvestorRow {
	id: string;
	name: string;
	email: string;
	phone: string;
	invested: number;
	round: string;
	shares: number;
	shareClass: string;
	price: number;
	issued: string;
	sessionId: string;
	session: any;
	fullStatus: Record<string, string>;
	raw: any;
}

const num = (v: unknown) => {
	const n = Number(v ?? 0);
	return Number.isFinite(n) ? n : 0;
};

const str = (v: unknown) => (v === undefined || v === null ? '' : String(v));

export const toInvestorRows = (list: any[]): IInvestorRow[] =>
	(list ?? []).map((f: any, i: number) => ({
		/* React warned "two children with the same key" on a real fund: the
		   payload repeats a sessionId across an investor's rows and some rows
		   carry no _id at all, so both fallbacks collide. The index makes it
		   unique without losing the stable part. */
		id: `${f?._id ?? f?.sessionId ?? 'row'}-${i}`,
		name: [f?.firstName, f?.lastName].filter(Boolean).join(' ').trim(),
		email: str(f?.email),
		phone: str(f?.phone) === '0' ? '' : str(f?.phone),
		invested: num(f?.invested),
		round: str(f?.fundingRound),
		shares: num(f?.share),
		shareClass: str(f?.class),
		price: num(f?.price),
		issued: str(f?.issueDate),
		sessionId: str(f?.sessionId),
		session: f?.session,
		fullStatus: f?.fullStatus ?? {},
		raw: f,
	}));

/** completed money: pasted rows count, session rows only once payIn/payOut is done */
export const totalRaised = (rows: IInvestorRow[]) =>
	rows.reduce((acc, r) => {
		const { payIn, payOut } = r.fullStatus ?? {};
		return !r.sessionId || [payIn, payOut].includes('completed')
			? acc + r.invested
			: acc;
	}, 0);

/** the complement — has a session, has not completed */
export const pendingRaised = (rows: IInvestorRow[]) =>
	rows.reduce((acc, r) => {
		const { payIn, payOut } = r.fullStatus ?? {};
		return r.sessionId && ![payIn, payOut].includes('completed')
			? acc + r.invested
			: acc;
	}, 0);

export const pendingInvestors = (rows: IInvestorRow[]) =>
	rows.filter(r => {
		const { payIn, payOut } = r.fullStatus ?? {};
		return r.sessionId && ![payIn, payOut].includes('completed');
	}).length;

/**
 * Is this money verified, or did someone type it in? The question the page
 * exists to answer and today only implies. A pasted row has no session at all.
 */
export const provenance = (
	r: IInvestorRow
): { verdict: 'ok' | 'rev' | 'rej' | 'pur' | 'ns'; label: string } => {
	if (!r.sessionId) return { verdict: 'ns', label: 'ADDED MANUALLY' };
	const { payIn, payOut } = r.fullStatus ?? {};
	if ([payIn, payOut].includes('completed'))
		return { verdict: 'ok', label: 'VERIFIED' };
	if ([payIn, payOut].includes('rejected') || [payIn, payOut].includes('failed'))
		return { verdict: 'rej', label: 'REJECTED' };
	if ([payIn, payOut].includes('processing'))
		return { verdict: 'rev', label: 'UNDER REVIEW' };
	return { verdict: 'pur', label: 'IN PROGRESS' };
};

/**
 * The investor's step chain. Design labels first — the same source the session
 * list reads — falling back to the fullStatus keys. Names are shortened through
 * the one shared map so this page names a step exactly as every other does.
 */
export const toSteps = (r: IInvestorRow): IStep[] => {
	const design: any[] = r.session?.design?.nodes ?? [];
	if (design.length) {
		return design.map((d: any, i: number) => ({
			key: `${r.id}-${d?.key ?? i}`,
			label: shortStep(d?.label ?? STEP_FALLBACK[d?.stepId] ?? d?.stepId ?? 'step'),
			state: 'pending' as const,
		}));
	}
	return Object.keys(r.fullStatus ?? {}).map((k, i) => ({
		key: `${r.id}-${k}-${i}`,
		label: shortStep(STEP_FALLBACK[k] ?? k),
		state: 'pending' as const,
	}));
};
